import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, Redirect, Link } from 'react-router-dom';
import jwt from 'jsonwebtoken';
import config from '../config/setting';
import Topbar from '../topbar/topbar';
import Sidebar from '../sidebar/sidebar';
import Footer from '../footer/footer';
import CheckChanges from '../confirmOnLeave';
import { logoutUser } from '../../redux/actions/user';
import { editCompany, getCompany } from '../../redux/actions/company';

const EditCompany: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const dispatch = useDispatch();

    const company = useSelector((state: any) => state.company.company);

    const [formData, setFormData] = useState({
        company_name: '',
        address: '',
        phone: '',
        email: '',
        status: '1',
    });
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidPhone, setIsValidPhone] = useState(true);

    // ✅ Fetch company when mounted
    useEffect(() => {
        dispatch(getCompany(Number(id)));
    }, [dispatch, id]);

    // ✅ Sync local state when company data updates
    useEffect(() => {
        if (company) {
            setFormData({
                company_name: company.company_name || '',
                address: company.address || '',
                phone: company.phone || '',
                email: company.email || '',
                status: company.status || '1',
            });
        }
    }, [company]);

    // ✅ Session validation
    if (!sessionStorage.token) return <Redirect to="/" />;
    jwt.verify(sessionStorage.token, config.secretKey, (err: any) => {
        if (err) dispatch(logoutUser());
    });

    // ✅ Handlers
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (value[0] !== ' ') {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validateEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const reg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setFormData((prev) => ({ ...prev, email: value }));
        setIsValidEmail(reg.test(value));
    };

    const validatePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.slice(0, 11).replace(/[^0-9]/g, '');
        setFormData((prev) => ({ ...prev, phone: value }));
        setIsValidPhone(/^\d+$/.test(value));
    };

    const isCompanyReady = () =>
        Object.values(formData).every((val) =>
            typeof val === 'string' ? val.trim() !== '' : val !== null && val !== undefined
        );


    const handleSaveBtnClick = () => {
        dispatch(editCompany(id, { ...formData, isActive: config.ACTIVE, history }));
    };

    return (
        <div className="page">
            <CheckChanges path="/edit-company" />
            <Topbar />
            <div className="page-content d-flex align-items-stretch">
                <Sidebar />
                <div className="content-inner">
                    <header className="page-header pb-1">
                        <div className="container-fluid d-flex justify-content-between align-items-center">
                            <h4>Company Management</h4>
                        </div>
                    </header>
                    <div className="breadcrumb-holder container-fluid">
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/companies" className="text-primary">Companies</Link>
                            </li>
                            <li className="breadcrumb-item active">Edit Company</li>
                        </ul>
                    </div>

                    <section className="forms">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card">
                                        <div className="card-body">
                                            {/* Form */}
                                            <div className="form-horizontal">
                                                <div className="row form-group">
                                                    <label className="col-md-3 font-weight-bold">Name<span className="text-danger">*</span></label>
                                                    <div className="col-md-9">
                                                        <input
                                                            id="company_name"
                                                            type="text"
                                                            name="company_name"
                                                            value={formData.company_name}
                                                            onChange={handleInputChange}
                                                            className="input-material"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row form-group">
                                                    <label className="col-md-3 font-weight-bold">Email<span className="text-danger">*</span></label>
                                                    <div className="col-md-9">
                                                        <input
                                                            id="email"
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={validateEmail}
                                                            className="input-material"
                                                            required
                                                        />
                                                        {!isValidEmail && formData.email && (
                                                            <small className="form-text text-danger">Invalid Email</small>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="row form-group">
                                                    <label className="col-md-3 font-weight-bold">Address<span className="text-danger">*</span></label>
                                                    <div className="col-md-9">
                                                        <input
                                                            id="address"
                                                            type="text"
                                                            name="address"
                                                            value={formData.address}
                                                            onChange={handleInputChange}
                                                            className="input-material"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div className="row form-group">
                                                    <label className="col-md-3 font-weight-bold">Contact<span className="text-danger">*</span></label>
                                                    <div className="col-md-9">
                                                        <input
                                                            id="phone"
                                                            type="text"
                                                            name="phone"
                                                            value={formData.phone}
                                                            onChange={validatePhone}
                                                            className="input-material"
                                                            required
                                                        />
                                                        {!isValidPhone && formData.phone && (
                                                            <small className="form-text text-danger">Invalid Phone Number</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-group float-right mt-3">
                                                <button
                                                    className="btn btn-primary"
                                                    disabled={!isCompanyReady()}
                                                    onClick={handleSaveBtnClick}
                                                >
                                                    Update Company
                                                </button>
                                            </div>
                                        </div>
                                    </div >
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default EditCompany;
